import sharedStateReducer from '../../../redux/reducers/sharedStateReducer';
import {createUndoConstructionAction} from './createUndoConstructionAction';
import {validateCurrentPlayerPendingBuildings} from './pendingBuildingsValidation';
import {checkIfRepairRecord, rewindConstructionState} from './rewindConstructionState';
import {
  selectBuildingsDamage,
  selectCurrentPlayer,
  selectHexes,
  selectPendingBuildings,
  selectSpecialBuildings,
} from '../../../redux/reducers/sharedStateReducer/selectors';

// Монеты текущего игрока плюс цены оставшихся построек хода. Отмена эту сумму
// не меняет: цена снятой постройки возвращается игроку, а льгота «первой фермы
// хода» при Аграрианизме удешевляет ферму-наследницу ровно на возвращенную
// переплату. Ремонт монет не стоит. Монеты без значения — ноль, как у редьюсера
const calcConstructionBalance = (state) => selectPendingBuildings(state).reduce(
  (balance, building) => balance + (checkIfRepairRecord(building) ? 0 : Number(building.price) || 0),
  Number(selectCurrentPlayer(state)?.coins) || 0
);

// Цена и isFreeAction в сверку не входят: их меняет передача льготы Аграрианизма
const checkIfSameRecord = (record, plannedRecord) => Boolean(record) && Boolean(plannedRecord)
  && record.type === plannedRecord.type
  && record.hexKey === plannedRecord.hexKey
  && record.token === plannedRecord.token;

const checkIfSameEntries = (a = {}, b = {}) => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].every((key) => a[key] === b[key]);
};

/**
 * Отменить действие с индексом index и убедиться, что отменилось ровно оно:
 * редьюсеры отмены наличие записи не проверяют (вернут цену из экшена и
 * перезапишут ячейку, даже если записи уже нет), а отмена постройки снимает
 * все записи с той же ячейкой и жетоном.
 *
 * Экшен строится из АКТУАЛЬНОЙ записи: к моменту отмены ее цену могла изменить
 * передача льготы Аграрианизма.
 */
const undoRecord = (getState, dispatch, index, plannedRecord) => {
  const stateBefore = getState();
  const record = selectPendingBuildings(stateBefore)[index];
  if (!checkIfSameRecord(record, plannedRecord)) {
    return false;
  }
  dispatch(createUndoConstructionAction(record));
  const stateAfter = getState();
  return selectPendingBuildings(stateAfter).length === selectPendingBuildings(stateBefore).length - 1
    && calcConstructionBalance(stateAfter) === calcConstructionBalance(stateBefore);
};

/**
 * Пробный прогон плана отмены (planConstructionRollback) на копии состояния
 * настоящими редьюсерами: каждая отмена снимает ровно свое действие, поле
 * пришло к тому же, что и модель планировщика, и проходит проверку конца хода.
 * Стор не трогается — редьюсеры отмены чистые, копией служит само состояние.
 *
 * removedIndexes — по убыванию: стек действий одной ячейки снимается сверху
 * вниз, а меньшие индексы от снятия больших не сдвигаются.
 */
export const checkConstructionUndoPlanOnCopy = (state, removedIndexes) => {
  const plannedRecords = selectPendingBuildings(state);
  // Действия одной ячейки сняты сверху вниз. Сверка поля этого не ловит:
  // дорогу из-под постройки этого же хода и редьюсер, и модель снимают
  // одинаково — стирая постройку с поля при живой записи о ней
  const removed = new Set(removedIndexes);
  const isEveryCellUndoneFromTop = removedIndexes.every((removedIndex) => plannedRecords.every(
    (building, index) => index <= removedIndex || removed.has(index)
      || building.hexKey !== plannedRecords[removedIndex].hexKey
  ));
  if (!isEveryCellUndoneFromTop) {
    return false;
  }

  let simulatedState = state;
  const getState = () => simulatedState;
  const dispatch = (action) => {
    simulatedState = {...simulatedState, shared: sharedStateReducer(simulatedState.shared, action)};
  };
  const isEveryUndone = removedIndexes.every((index) => undoRecord(getState, dispatch, index, plannedRecords[index]));
  if (!isEveryUndone) {
    return false;
  }

  const modelState = rewindConstructionState(state, removed);
  return checkIfSameEntries(selectHexes(simulatedState), selectHexes(modelState))
    && checkIfSameEntries(selectSpecialBuildings(simulatedState), selectSpecialBuildings(modelState))
    && checkIfSameEntries(selectBuildingsDamage(simulatedState), selectBuildingsDamage(modelState))
    && validateCurrentPlayerPendingBuildings(simulatedState).isValid;
};

/**
 * Применить план отмены к настоящему стору. Останавливается на первом шаге,
 * который прошел не так, как в пробном прогоне, и возвращает false — дальше
 * ход дожимает откат с конца, он читает стор заново.
 */
export const applyConstructionUndoPlan = (store, removedIndexes, plannedRecords) => {
  const getState = () => store.getState();
  const dispatch = (action) => store.dispatch(action);
  return removedIndexes.every((index) => undoRecord(getState, dispatch, index, plannedRecords[index]));
};
